import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, SLUGS } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Machines from "@/pages/Machines";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Inquiry from "@/pages/Inquiry";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const PAGES = [
  { key: "home", el: <Home /> },
  { key: "services", el: <Services /> },
  { key: "machines", el: <Machines /> },
  { key: "gallery", el: <Gallery /> },
  { key: "about", el: <About /> },
  { key: "contact", el: <Contact /> },
  { key: "inquiry", el: <Inquiry /> },
  { key: "imprint", el: <Legal kind="imprint" /> },
  { key: "privacy", el: <Legal kind="privacy" /> },
  { key: "cookies", el: <Legal kind="cookies" /> },
];

function SiteLayout({ children }) {
  return (
    <div className="min-h-screen bg-anthracite-dark text-slate-100">
      <Header />
      <main>{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function LangRedirect() {
  const browser = (navigator.language || "de").toLowerCase();
  return <Navigate to={browser.startsWith("it") ? "/it" : "/de"} replace />;
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LenisRoot() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LangRedirect />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      {["de", "it"].flatMap((l) =>
        PAGES.map((p) => {
          const slug = SLUGS[l][p.key];
          return (
            <Route
              key={`${l}-${p.key}`}
              path={`/${l}${slug ? `/${slug}` : ""}`}
              element={<SiteLayout>{p.el}</SiteLayout>}
            />
          );
        })
      )}
      <Route path="*" element={<LangRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LenisRoot />
      <ScrollManager />
      <LangProvider>
        <AppRoutes />
        <Toaster position="bottom-right" theme="dark" />
      </LangProvider>
    </BrowserRouter>
  );
}
